const randomFromArray = (list = [], fallback = null) => {
  if (!Array.isArray(list) || list.length === 0) {
    return fallback;
  }
  const index = Math.floor(Math.random() * list.length);
  return list[index] ?? fallback;
};

const extractFirstName = (fullName = "") => {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return "";
  }
  const [firstName] = trimmed.split(/\s+/);
  return firstName || "";
};

const shouldApplyChange = (probability = 0.5) => Math.random() < probability;

const SYNONYM_GROUPS = [
  ["plataforma", "ambiente", "solução"],
  ["processo", "fluxo", "trâmite"],
  ["formulário", "formulário", "form"],
  ["robô", "automação", "bot"],
  ["relatório", "sumário", "documento"],
  ["configuração", "ajuste", "parametrização"],
  ["usuário", "colaborador", "participante"],
  ["tarefa", "etapa", "atividade"],
  ["dados", "informações", "conteúdo"],
  ["painel", "dashboard", "visão"],
  ["análise", "avaliação", "leitura"],
  ["detalhe", "ponto", "aspecto"],
  ["exemplo", "amostra", "referência"],
  ["ajuda", "apoio", "suporte"],
  ["explicar", "detalhar", "esclarecer"],
  ["mostrar", "apresentar", "demonstrar"],
  ["resumo", "síntese", "sumário"],
  ["importante", "relevante", "fundamental"],
  ["rápido", "ágil", "breve"],
  ["enviar", "compartilhar", "remeter"],
  ["concluir", "finalizar", "encerrar"],
  ["passo", "etapa", "fase"],
  ["apontar", "indicar", "destacar"],
  ["verificar", "checar", "confirmar"],
];

const PREFIXES = [
  "Para explicar de outro jeito,",
  "Falando de forma direta,",
  "De uma forma bem objetiva,",
  "Traduzindo para o nosso dia a dia,",
  "Em outras palavras,",
  "Resumindo o contexto,",
  "Pra gente alinhar rápido,",
  "Colocando em termos simples,",
  "Pra te contextualizar,",
  "Indo direto ao ponto,",
];

const SUFFIXES = [
  "Se quiser seguir por outro caminho, é só me chamar.",
  "Qualquer novidade é só falar comigo.",
  "Sigo por aqui caso queira mergulhar em mais detalhes.",
  "Fico à disposição para ajustar o que precisar.",
  "Quando quiser revisar outro ponto, é só avisar.",
  "Caso precise de outra explicação, é só acenar.",
  "Me chama novamente quando quiser evoluir o assunto.",
  "Podemos explorar outra vertente assim que você quiser.",
  "Posso continuar destrinchando o tema sempre que pedir.",
];

const GREETING_VARIATIONS = [
  "Olá, como posso ajudar hoje?",
  "Oi, em que posso ajudar agora?",
  "Olá, precisa de alguma coisa hoje?",
  "Oi, me diz como posso ajudar.",
  "Olá, quer ajuda com algo agora?",
  "Oi, como posso apoiar você hoje?",
  "Olá, posso ajudar em algo agora?",
  "Oi, tem algo que eu possa fazer por você?",
  "Olá, no que posso ajudar?",
  "Oi, por onde começo a ajudar?",
  "Olá, como posso ajudar neste momento?",
  "Oi, estou aqui para ajudar, o que precisa?",
  "Olá, diga como posso ajudar.",
  "Oi, quer que eu ajude com algo?",
  "Olá, posso dar uma mão em algo?",
  "Oi, qual ajuda você precisa agora?",
  "Olá, qual é a necessidade de hoje?",
  "Oi, posso apoiar em alguma tarefa?",
  "Olá, me fala do que precisa.",
  "Oi, em que posso ser útil agora?",
  "Olá, tem algo em que eu possa contribuir?",
  "Oi, quer apoio em algo específico?",
  "Olá, estou pronta para ajudar, qual o tema?",
  "Olá, posso resolver algo para você?",
  "Oi, o que você gostaria que eu fizesse?",
  "Olá, qual assunto precisa de ajuda?",
  "Oi, como posso colaborar hoje?",
  "Olá, me chama para ajudar no que for preciso.",
  "Oi, descreva como posso ajudar.",
  "Olá, posso começar ajudando por onde?",
  "Oi, tem alguma dúvida que eu possa tirar?",
  "Olá, qual é sua prioridade agora?",
  "Oi, quer que eu cuide de alguma coisa?",
  "Olá, quer que eu acompanhe algum assunto?",
  "Oi, me avise como posso contribuir.",
  "Olá, podemos resolver algo juntos agora?",
  "Oi, existe algo que precise de atenção?",
  "Olá, o que posso organizar para você?",
  "Oi, tem um tema que queira que eu trate?",
  "Olá, estou por aqui para ajudar no que precisar.",
  "Oi, qual apoio você espera agora?",
  "Olá, posso orientar em alguma etapa?",
  "Oi, quer sugestões ou ajuda direta?",
  "Olá, me conte o que precisa resolver.",
  "Oi, estou pronta para auxiliar, qual o ponto?",
  "Olá, podemos começar por alguma tarefa?",
  "Olá, qual problema quer resolver agora?",
  "Oi, tem algo urgente que eu possa ajudar?",
  "Olá, por qual assunto devo começar?",
  "Oi, como posso facilitar seu trabalho hoje?",
];

const SENTENCE_TRANSITIONS = [];

const SENTENCE_EMPHASIS = [];

const protectCodeBlocks = (text) => {
  const placeholders = [];
  let protectedText = text;

  const storePlaceholder = (match) => {
    const key = `__CODE_BLOCK_${placeholders.length}__`;
    placeholders.push({ key, value: match });
    return key;
  };

  protectedText = protectedText.replace(/```[\s\S]*?```/g, storePlaceholder);
  protectedText = protectedText.replace(/`[^`]+`/g, storePlaceholder);

  return { protectedText, placeholders };
};

const restoreCodeBlocks = (text, placeholders = []) => {
  let restored = text;
  placeholders.forEach(({ key, value }) => {
    restored = restored.replace(key, value);
  });
  return restored;
};

const lowercaseFirstLetter = (value = "") => {
  if (!value) {
    return value;
  }
  return value.charAt(0).toLowerCase() + value.slice(1);
};

const attachConnector = (connector, sentence) => {
  if (!connector?.text) {
    return sentence;
  }
  const trimmed = sentence.trim();
  if (!trimmed) {
    return sentence;
  }
  const shouldLowercase = Boolean(connector.lowercaseNext);
  const formatted = shouldLowercase ? lowercaseFirstLetter(trimmed) : trimmed;
  return `${connector.text} ${formatted}`;
};

const preserveCase = (original, replacement) => {
  if (original.toUpperCase() === original) {
    return replacement.toUpperCase();
  }
  if (original[0] === original[0]?.toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
};

const applySynonymReplacements = (paragraph) => {
  return SYNONYM_GROUPS.reduce((result, group) => {
    const pattern = new RegExp(`\\b(${group.join("|")})\\b`, "gi");
    return result.replace(pattern, (match) => {
      if (!shouldApplyChange(0.5)) {
        return match;
      }
      const candidates = group.filter((item) => item.toLowerCase() !== match.toLowerCase());
      if (candidates.length === 0) {
        return match;
      }
      const replacement = randomFromArray(candidates, match);
      return preserveCase(match, replacement);
    });
  }, paragraph);
};

const shouldKeepStructure = (paragraph) => {
  if (paragraph.trim().length < 80) {
    return false;
  }
  return /\n|•|-|\d+\.|:/.test(paragraph);
};

const shuffleArray = (list = []) => {
  const copy = [...list];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }
  return copy;
};

const shuffleSentences = (paragraph) => {
  const sentences = paragraph
    .split(/(?<=[.!?])\s+(?=[A-ZÀ-Ü0-9])/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  if (sentences.length <= 1) {
    return paragraph;
  }
  const [firstSentence, ...others] = sentences;
  if (others.length === 0) {
    return paragraph;
  }
  const shuffled = shuffleArray(others);
  const finalSentences = shouldApplyChange(0.3)
    ? [...shuffled, firstSentence]
    : [firstSentence, ...shuffled];
  return finalSentences.join(" ");
};

const applySentenceConnectors = (paragraph) => {
  const sentences = paragraph
    .split(/(?<=[.!?])\s+(?=[A-ZÀ-Ü0-9])/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

  if (sentences.length === 0) {
    return paragraph;
  }

  const transformed = sentences.map((sentence, index) => {
    let nextSentence = sentence;
    if (index === sentences.length - 1 && shouldApplyChange(0.4)) {
      const connector = randomFromArray(SENTENCE_EMPHASIS, null);
      nextSentence = attachConnector(connector, nextSentence);
      return nextSentence;
    }
    if (shouldApplyChange(0.35)) {
      const connector = randomFromArray(SENTENCE_TRANSITIONS, null);
      nextSentence = attachConnector(connector, nextSentence);
      return nextSentence;
    }
    return nextSentence;
  });

  return transformed.join(" ");
};

export const buildVariedText = (text, options = {}) => {
  if (typeof text !== "string" || !text.trim()) {
    return text;
  }
  const { disableConnectors = false } = options;
  const trimmed = text.trim();
  const { protectedText, placeholders } = protectCodeBlocks(trimmed);
  const paragraphs = protectedText.split(/\n{2,}/);
  const variedParagraphs = paragraphs.map((paragraph) => {
    if (!paragraph.trim()) {
      return paragraph;
    }
    let result = paragraph;
    const structured = shouldKeepStructure(paragraph);
    if (!structured) {
      result = shuffleSentences(result);
      result = applySentenceConnectors(result);
    }
    result = applySynonymReplacements(result);
    return result;
  });
  let combined = variedParagraphs.join("\n\n");

  combined = restoreCodeBlocks(combined, placeholders);

  if (!disableConnectors) {
    const prefix = shouldApplyChange(0.7) ? randomFromArray(PREFIXES, null) : null;
    const suffix = shouldApplyChange(0.7) ? randomFromArray(SUFFIXES, null) : null;
    combined = [prefix, combined, suffix].filter(Boolean).join(" ").trim();
  }

  return combined;
};

export const buildVariedGreeting = (userName = "") => {
  const firstName = extractFirstName(userName);
  const personalizedGreetings = firstName
    ? [
        `Olá, ${firstName}, como posso ajudar hoje?`,
        `Oi, ${firstName}, em que posso ajudar agora?`,
        `Tudo bem, ${firstName}? Precisa de ajuda com algo?`,
        `${firstName}, me diz como posso ajudar hoje.`,
        `Posso ajudar com algo agora, ${firstName}?`,
      ]
    : GREETING_VARIATIONS;

  const baseGreeting = randomFromArray(
    personalizedGreetings,
    personalizedGreetings[0] ?? GREETING_VARIATIONS[0]
  );
  return buildVariedText(baseGreeting, { disableConnectors: true });
};

export const buildVariedFallback = (text) => buildVariedText(text, { disableConnectors: false });

export const getRandomFromList = randomFromArray;
